import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { getServicioStyle } from "../../utils/servicioColores";

const TIPO_LABEL = { admin_asistentas: "Admin Asistentas 🧹", admin_atcliente: "Admin At.Cliente 🤝", admin_servicios: "Admin Servicios 🏠" };
const TIPO_COLOR = { admin_asistentas: "#ea580c", admin_atcliente: "#7c3aed", admin_servicios: "#059669" };
const ORDEN_SERVICIOS = ["PRIME", "TI", "PLUS", "G", "YP", "CTA"];
const ordenServicio = (servicio) => {
  const i = ORDEN_SERVICIOS.indexOf(servicio);
  return i === -1 ? ORDEN_SERVICIOS.length : i;
};

const AdminSupervisorasEncargadas = () => {
  const { adminUser } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const rolActual = authUser?.tipo || "";
  // Solo el superadmin puede asignar/quitar supervisoras encargadas.
  // Un admin_asistentas / admin_atcliente / admin_servicios que entra a "Mis
  // Supervisoras" solo puede consultar quiénes son las suyas.
  const puedeEditar = rolActual === "superadmin";

  const [adminInfo, setAdminInfo]         = useState(null);
  const [encargadas, setEncargadas]       = useState([]);
  const [disponibles, setDisponibles]     = useState([]);
  const [pisosPorSup, setPisosPorSup]     = useState({}); // { [supervisora_user]: { total_pisos, pisos: [{id,abreviatura,servicio}] } }
  const [loading, setLoading]             = useState(true);
  const [loadingSup, setLoadingSup]       = useState(true);
  const [error, setError]                 = useState("");
  const [busqueda, setBusqueda]           = useState("");
  const [accionando, setAccionando]       = useState(null); // user string que está en proceso

  // Un solo clic despliega, por supervisora, el desglose por servicio con sus
  // pisos ya visibles debajo (sin un segundo nivel de clics).
  const [expandido, setExpandido] = useState({}); // { [supervisora_user]: bool }
  const toggleExpandido = (sup) => setExpandido((e) => ({ ...e, [sup]: !e[sup] }));

  const desgloseServicio = useCallback((sup) => {
    const info = pisosPorSup[sup];
    if (!info) return [];
    const mapa = new Map();
    info.pisos.forEach((p) => {
      const key = p.servicio || "Sin servicio";
      if (!mapa.has(key)) mapa.set(key, []);
      mapa.get(key).push(p);
    });
    return Array.from(mapa.entries())
      .map(([servicio, pisos]) => ({ servicio, pisos }))
      .sort((a, b) => ordenServicio(a.servicio) - ordenServicio(b.servicio));
  }, [pisosPorSup]);

  const cargarTodo = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [resAdmin, resEnc, resPisos] = await Promise.all([
        axios.get(`/api/usuarios/${adminUser}`),
        axios.get(`/api/usuarios/encargadas/${adminUser}`),
        axios.get(`/api/usuarios/encargadas/${adminUser}/pisos`),
      ]);
      setAdminInfo(resAdmin.data.data || null);
      setEncargadas(resEnc.data.data || []);
      const mapa = {};
      (resPisos.data.data || []).forEach((d) => { mapa[d.supervisora_user] = d; });
      setPisosPorSup(mapa);
    } catch (e) {
      setError("Error al cargar los datos.");
    } finally {
      setLoading(false);
    }
  }, [adminUser]);

  const cargarSupervisoras = useCallback(async () => {
    setLoadingSup(true);
    try {
      const res = await axios.get("/api/usuarios/?tipo=supervisora");
      setDisponibles(res.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSup(false);
    }
  }, []);

  useEffect(() => { cargarTodo(); if (puedeEditar) cargarSupervisoras(); else setLoadingSup(false); }, [cargarTodo, cargarSupervisoras, puedeEditar]);

  const asignar = async (supervisora_user) => {
    setAccionando(supervisora_user);
    setError("");
    try {
      await axios.post(`/api/usuarios/encargadas/${adminUser}`, { supervisora_user });
      await cargarTodo();
    } catch (e) {
      setError(e.response?.data?.error || "Error al asignar.");
    } finally {
      setAccionando(null);
    }
  };

  const quitar = async (supervisora_user) => {
    if (!window.confirm(`¿Quitar a ${supervisora_user} como encargada de ${adminUser}?`)) return;
    setAccionando(supervisora_user);
    setError("");
    try {
      await axios.delete(`/api/usuarios/encargadas/${adminUser}/${supervisora_user}`);
      await cargarTodo();
    } catch (e) {
      setError(e.response?.data?.error || "Error al quitar.");
    } finally {
      setAccionando(null);
    }
  };

  const encargadasSet = new Set(encargadas.map(e => e.supervisora_user));
  const noAsignadas = disponibles.filter(
    s => !encargadasSet.has(s.user) &&
         (busqueda === "" || s.user.toLowerCase().includes(busqueda.toLowerCase()) || (s.nombre || "").toLowerCase().includes(busqueda.toLowerCase()))
  );

  const tipoColor = adminInfo ? (TIPO_COLOR[adminInfo.tipo] || "#374151") : "#374151";
  const tipoLabel = adminInfo ? (TIPO_LABEL[adminInfo.tipo] || adminInfo.tipo) : "";

  return (
    <div style={s.page}>
      {/* Cabecera */}
      <div style={s.header}>
        <button onClick={() => navigate(-1)} style={s.btnVolver}>← Volver</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.55)" }}>👩‍💼 Mis Supervisoras</div>
          {adminInfo ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
              <strong style={{ fontSize: 24, color: "#fff" }}>{adminInfo.nombre || adminUser}</strong>
              <span style={{ fontSize: 13, background: tipoColor, color: "#fff", borderRadius: 10, padding: "4px 12px", fontWeight: 700 }}>
                {tipoLabel}
              </span>
            </div>
          ) : (
            <strong style={{ fontSize: 24, color: "#fff" }}>{adminUser}</strong>
          )}
        </div>
        {!puedeEditar && (
          <div style={s.totalBadge}>
            <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{encargadas.length}</div>
            <div style={{ fontSize: 11, opacity: 0.75 }}>supervisora{encargadas.length !== 1 ? "s" : ""}</div>
          </div>
        )}
      </div>

      {error && (
        <div style={s.errorBox}>{error}</div>
      )}

      {loading ? (
        <p style={{ textAlign: "center", color: "#64748b", padding: 60, fontSize: 16 }}>Cargando…</p>
      ) : (
        <div style={s.content}>
          {/* Encargadas actuales */}
          {encargadas.length === 0 ? (
            <div style={s.vacio}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🗂️</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#475569" }}>
                {puedeEditar ? "Sin supervisoras asignadas todavía" : "Sin supervisoras asignadas todavía"}
              </div>
              {puedeEditar && <div style={{ fontSize: 13.5, color: "#94a3b8", marginTop: 4 }}>Añade una desde la lista de abajo.</div>}
            </div>
          ) : (
            <div style={s.grid}>
              {encargadas.map((e) => {
                const info = pisosPorSup[e.supervisora_user];
                const abierto = !!expandido[e.supervisora_user];
                const desglose = abierto ? desgloseServicio(e.supervisora_user) : [];
                return (
                  <div key={e.supervisora_user} style={{ ...s.card, ...(abierto ? s.cardAbierta : {}) }}>
                    <div style={s.cardTop} onClick={() => toggleExpandido(e.supervisora_user)}>
                      <div style={s.avatarEnc}>{(e.nombre || e.supervisora_user)[0].toUpperCase()}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={s.nombreSup}>{e.nombre ? `${e.nombre} ${e.apellidos || ""}`.trim() : e.supervisora_user}</div>
                        {e.nombre && <div style={s.userSup}>@{e.supervisora_user}</div>}
                      </div>
                      <div style={s.statPisos}>
                        <div style={{ fontSize: 26, fontWeight: 800, color: "#4338ca", lineHeight: 1 }}>{info ? info.total_pisos : "…"}</div>
                        <div style={{ fontSize: 11.5, color: "#6366f1", fontWeight: 700 }}>piso{info?.total_pisos === 1 ? "" : "s"}</div>
                      </div>
                      <span style={{ ...s.chevron, transform: abierto ? "rotate(180deg)" : "none" }}>▾</span>
                    </div>

                    {abierto && (
                      <div style={s.desglose}>
                        {desglose.length === 0 ? (
                          <div style={{ fontSize: 14, color: "#94a3b8", padding: "6px 2px" }}>Sin pisos activos.</div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {desglose.map(({ servicio, pisos }) => {
                              const sc = getServicioStyle(servicio);
                              return (
                                <div key={servicio}>
                                  <span style={{ ...s.badgeServicio, background: sc.bg, color: sc.color, border: `1.5px solid ${sc.border}` }}>
                                    {servicio} · {pisos.length}
                                  </span>
                                  <div style={s.pisosFila}>
                                    {pisos.map((p) => (
                                      <span key={p.id} style={s.pisoTag}>{p.abreviatura}</span>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {puedeEditar && (
                      <button
                        onClick={(ev) => { ev.stopPropagation(); quitar(e.supervisora_user); }}
                        disabled={accionando === e.supervisora_user}
                        style={s.btnQuitar}
                      >
                        {accionando === e.supervisora_user ? "Quitando…" : "✕ Quitar de encargadas"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Supervisoras disponibles */}
          {puedeEditar && (
          <div style={s.section}>
            <h3 style={s.sectionTitle}>➕ Añadir supervisora</h3>
            <input
              type="text"
              placeholder="Buscar supervisora…"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              style={s.buscador}
            />
            {loadingSup ? (
              <p style={{ color: "#94a3b8", fontSize: 14 }}>Cargando supervisoras…</p>
            ) : noAsignadas.length === 0 ? (
              <p style={{ color: "#94a3b8", fontSize: 14, textAlign: "center", padding: "12px 0" }}>
                {busqueda ? "Sin resultados." : "Todas las supervisoras ya están asignadas."}
              </p>
            ) : (
              <div style={s.lista}>
                {noAsignadas.map((sup) => (
                  <div key={sup.user} style={s.itemDisp}>
                    <div style={s.avatarDisp}>{(sup.nombre || sup.user)[0].toUpperCase()}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{sup.user}</div>
                      {sup.nombre && <div style={{ fontSize: 13, color: "#64748b" }}>{sup.nombre} {sup.apellidos || ""}</div>}
                    </div>
                    <button
                      onClick={() => asignar(sup.user)}
                      disabled={accionando === sup.user}
                      style={s.btnAsignar}
                    >
                      {accionando === sup.user ? "…" : "➕ Asignar"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
};

const s = {
  page:    { minHeight: "100vh", background: "#f0f4f8", fontFamily: "Segoe UI, sans-serif" },
  header:  { background: "linear-gradient(135deg,#1e293b,#0f172a)", color: "#fff", padding: "20px 24px", display: "flex", alignItems: "center", gap: 16 },
  btnVolver: { background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "10px 16px", whiteSpace: "nowrap" },
  totalBadge: { textAlign: "center", background: "rgba(255,255,255,0.1)", borderRadius: 14, padding: "8px 18px", color: "#fff" },
  errorBox: { background: "#fee2e2", color: "#b91c1c", borderRadius: 10, padding: "12px 18px", margin: "18px 24px 0", fontSize: 14 },
  content:  { padding: "24px", maxWidth: 780, margin: "0 auto", display: "flex", flexDirection: "column", gap: 28 },

  vacio: { background: "#fff", borderRadius: 18, boxShadow: "0 2px 10px rgba(15,23,42,0.06)", padding: "40px 20px", textAlign: "center" },

  grid: { display: "flex", flexDirection: "column", gap: 14 },
  card: { background: "#fff", borderRadius: 18, boxShadow: "0 2px 12px rgba(15,23,42,0.07)", border: "1.5px solid #eef2f7", overflow: "hidden", transition: "box-shadow .15s" },
  cardAbierta: { boxShadow: "0 8px 24px rgba(67,56,202,0.14)", border: "1.5px solid #c7d2fe" },
  cardTop: { display: "flex", alignItems: "center", gap: 16, padding: "18px 20px", cursor: "pointer" },
  avatarEnc: { width: 54, height: 54, borderRadius: "50%", background: "linear-gradient(135deg,#34d399,#10b981)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 22, flexShrink: 0 },
  nombreSup: { fontWeight: 800, fontSize: 18, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  userSup: { fontSize: 13, color: "#94a3b8", marginTop: 1 },
  statPisos: { textAlign: "center", minWidth: 64 },
  chevron: { fontSize: 18, color: "#94a3b8", transition: "transform .2s", marginLeft: 4 },

  desglose: { padding: "4px 20px 20px", borderTop: "1px solid #f1f5f9", marginTop: 2, paddingTop: 16 },
  badgeServicio: { display: "inline-block", padding: "5px 14px", borderRadius: 20, fontSize: 13.5, fontWeight: 800 },
  pisosFila: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 },
  pisoTag: { fontSize: 13, fontWeight: 600, color: "#334155", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "4px 10px" },

  btnQuitar: { display: "block", width: "100%", padding: "12px 0", border: "none", borderTop: "1px solid #fee2e2", background: "#fff5f5", color: "#b91c1c", cursor: "pointer", fontWeight: 700, fontSize: 13.5 },

  section:  { background: "#fff", borderRadius: 18, boxShadow: "0 2px 10px rgba(15,23,42,0.06)", padding: "22px 24px" },
  sectionTitle: { fontSize: 17, fontWeight: 800, color: "#1e293b", marginBottom: 16, margin: "0 0 16px 0" },
  lista:    { display: "flex", flexDirection: "column", gap: 10 },
  itemDisp: { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0" },
  avatarDisp: { width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg,#c084fc,#a855f7)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18, flexShrink: 0 },
  btnAsignar: { padding: "8px 16px", borderRadius: 10, border: "1px solid #6ee7b7", background: "#d1fae5", color: "#065f46", cursor: "pointer", fontWeight: 700, fontSize: 14 },
  buscador:   { width: "100%", padding: "12px 16px", borderRadius: 12, border: "1.5px solid #e2e8f0", fontSize: 15, marginBottom: 14, boxSizing: "border-box", outline: "none" },
};

export default AdminSupervisorasEncargadas;
